import {
  enforceConsent,
  readConsent,
  resetConsentCache,
  subscribeConsent,
  type ConsentDecision,
} from '@redbrickmedia/duplex.user-identity'
import { isDebugEnabled } from '../debug/isDebugEnabled'
import debug from '../console'
import { markConsentTiming, measureConsentTiming } from './consentTiming'
import {
  ensureUsOptOutSignal,
  resetUsOptOutSignalForTests,
} from './usOptOutSignal'

const logger = debug.withTag('utils:consent')

let pending: Promise<ConsentDecision> | null = null
let current: ConsentDecision | null = null
let stopPackageSubscription: (() => void) | null = null
const subscribers = new Set<(decision: ConsentDecision) => void>()

function publish(decision: ConsentDecision): void {
  if (current === decision) return

  current = decision
  markConsentTiming(`decision-${decision.state}`, true)
  logger.debug(
    `Consent resolved: ${decision.state} (policy: ${decision.policy}, evidence: ${decision.evidence.join(',') || 'none'})`
  )
  subscribers.forEach((subscriber) => {
    try {
      subscriber(decision)
    } catch (error) {
      logger.warn('Consent subscriber failed', error)
    }
  })
}

function unknownErrorDecision(): ConsentDecision {
  return {
    state: 'unknown',
    policy: 'unknown',
    policies: ['unknown'],
    reason: 'error',
    evidence: ['error'],
    identity: 'unknown',
    firstPartyId: 'unknown',
    restrictions: {
      restrictDataProcessing: true,
      nonPersonalizedAds: true,
    },
    revisable: true,
  }
}

function start(country?: string): Promise<ConsentDecision> {
  const options = {
    country,
    debug: isDebugEnabled(),
  }

  if (!pending) {
    markConsentTiming('consent-read-start', true)
    try {
      ensureUsOptOutSignal()
      stopPackageSubscription ??= subscribeConsent(publish, options)
      markConsentTiming('consent-api-read-start', true)
      pending = readConsent(options)
        .then((decision) => {
          publish(decision)
          markConsentTiming('consent-read-settled', true)
          measureConsentTiming(
            'consent-read',
            'consent-read-start',
            'consent-read-settled'
          )
          return decision
        })
        .catch((error) => {
          logger.warn('Consent read failed; preserving unknown state', error)
          const decision = unknownErrorDecision()
          enforceConsent(decision, isDebugEnabled())
          publish(decision)
          markConsentTiming('consent-read-settled', true)
          measureConsentTiming(
            'consent-read',
            'consent-read-start',
            'consent-read-settled'
          )
          return decision
        })
    } catch (error) {
      logger.warn(
        'Consent initialization failed; preserving unknown state',
        error
      )
      const decision = unknownErrorDecision()
      enforceConsent(decision, isDebugEnabled())
      publish(decision)
      markConsentTiming('consent-read-settled', true)
      measureConsentTiming(
        'consent-read',
        'consent-read-start',
        'consent-read-settled'
      )
      pending = Promise.resolve(decision)
    }
  }

  return pending
}

export function resolveConsent(country?: string): Promise<ConsentDecision> {
  return start(country)
}

export function subscribeToConsent(
  subscriber: (decision: ConsentDecision) => void,
  country?: string
): () => void {
  subscribers.add(subscriber)
  if (current) {
    try {
      subscriber(current)
    } catch (error) {
      logger.warn('Consent subscriber failed', error)
    }
  }
  void start(country)

  return () => subscribers.delete(subscriber)
}

export async function whenConsentSettled(): Promise<void> {
  await resolveConsent()
}

export async function settledConsentDecision(): Promise<ConsentDecision> {
  await resolveConsent()
  return current ?? unknownErrorDecision()
}

export function consentSnapshot(): ConsentDecision | null {
  return current
}

export function resetConsentGateForTests(): void {
  stopPackageSubscription?.()
  stopPackageSubscription = null
  pending = null
  current = null
  subscribers.clear()
  resetConsentCache()
  resetUsOptOutSignalForTests()
}

export type { ConsentDecision }
